import { BASE_URL } from '../../../utils/constants/url-constants';
import { getSRPHub } from '../../../seo/srpAllowlist';
import { decodeLocationSlug } from '../../../utils/location/decode-location-slug';
import { toLocationSlug } from '../../../utils/location/to-location-slug';
import { buildEncodedLocationSrpPathSegment } from '../../../utils/location/build-encoded-location-srp-path-segment';
import { encodeSrpPathForUrl } from '../../../utils/routing/encode-srp-path-for-url';
import { generateSEOMeta, generateTitle } from '../../../utils/seo/generate-srp-seo-meta';
import { generateSRPCanonicalUrl, extractPageNumber, pageParamForPageNumber } from '../../../utils/seo/srp-seo-utils';
import { buildActiveFiltersFromInitialFilters } from '../../../utils/seo/build-active-filters';
import type { AggregatedJobData } from '../../../types/srp-seo-meta';
import { applyLocationSalaryAggregate, buildAggregatedJobDataFromSearchResults } from '../../../utils/seo/build-aggregated-job-data-from-search-results';
import { getStaticUrlSeoOverride } from '../../../utils/seo/get-static-url-seo-override';
import { truncateSeoTitle } from '../../../utils/seo/truncate-seo-title';
import { getCategorySlugByFieldValue } from '../../../utils/category/category-hub-config';
import { isKnownFinlandLocationSlug } from '../../../utils/location/finland-hub-config';
import { isGazetteerFinnishTownSlug } from '../../../utils/location/is-gazetteer-finnish-town-slug';
import { isRemoteRegionalScopeForJobCard } from '../../../utils/geocoding';

type SupportedLocale = 'en' | 'fi' | 'sv';

const SUPPORTED_LOCALES: SupportedLocale[] = ['en', 'fi', 'sv'];
const JOBS_SEGMENT_BY_LOCALE: Record<SupportedLocale, string> = {
  en: 'jobs',
  fi: 'tyopaikat',
  sv: 'lediga-jobb',
};
const LOCATION_SEGMENT_BY_LOCALE: Record<SupportedLocale, string> = {
  en: 'location',
  fi: 'alue',
  sv: 'omrade',
};
const CATEGORY_SEGMENT_BY_LOCALE: Record<SupportedLocale, string> = {
  en: 'category',
  fi: 'kategoria',
  sv: 'kategori',
};

export interface SearchPageMeta {
  canonicalUrl: string;
  title: string;
  description: string;
}

/**
 * Truncates a title to a maximum length, breaking at word boundaries when possible.
 * Preserves a trailing `| Jobcrawls` segment when shortening (see truncateSeoTitle).
 */
export function truncateTitle(title: string, maxLength: number = 60): string {
  return truncateSeoTitle(title, maxLength);
}

/**
 * Truncates a description to a target length, breaking at sentence boundaries when possible
 */
export function truncateDescription(description: string, targetLength: number = 160, minLength: number = 150): string {
  if (description.length <= targetLength) {
    return description;
  }

  const sentenceMatch = description.substring(0, targetLength).match(/.*[.!?]\s/);
  if (sentenceMatch) {
    const result = sentenceMatch[0].trim();
    if (result.length >= minLength) {
      return result;
    }
  }

  const truncated = description.substring(0, targetLength);
  const lastSpace = truncated.lastIndexOf(' ');
  if (lastSpace >= minLength) {
    return truncated.substring(0, lastSpace).trim();
  }

  return description.substring(0, targetLength).trim() + '...';
}

/**
 * Creates a URL-friendly slug from a location string
 */
export function createLocationSlug(raw: string | null | undefined): string {
  if (!raw) return '';
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    // ignore decoding issues
  }
  return toLocationSlug(decoded);
}

/**
 * Decodes a location value from URL format to display format
 */
export function decodeLocationValue(raw: string | null | undefined): string {
  return decodeLocationSlug(raw);
}

/**
 * Gets location from filters and route parameters
 */
export function getLocationFromFiltersAndRoute(filters: unknown, pageContext: any): string | null {
  const parsedFilters =
    filters && typeof filters === 'object'
      ? (filters as any)
      : typeof filters === 'string'
        ? JSON.parse(filters || '{}')
        : {};
  const locationFilters = parsedFilters.location || [];

  const hasLocationFilter = Array.isArray(locationFilters) && locationFilters.length === 1;
  const hasLocationProperty = parsedFilters.hasOwnProperty('location');

  if (hasLocationProperty) {
    return hasLocationFilter ? locationFilters[0] : null;
  }

  if (hasLocationFilter) {
    return locationFilters[0];
  }

  const routeLocation = pageContext?.routeParams?.location;
  if (typeof routeLocation === 'string' && routeLocation.trim()) {
    try {
      const decoded = decodeLocationSlug(routeLocation);
      return decoded || null;
    } catch {
      return null;
    }
  }

  return null;
}

/** " (Page N)" title suffix, per locale — shared by the SSR title (+title.ts) and the
 *  client-side live-state title below so the two never drift apart. */
export function getSearchPageTitleSuffix(page: number, locale: SupportedLocale): string {
  if (locale === 'fi') return ` (Sivu ${page})`;
  if (locale === 'sv') return ` (Sida ${page})`;
  return ` (Page ${page})`;
}

/**
 * Client-side counterpart to computeSearchPageMeta's title logic. SRP filter/query/sort/page/map
 * changes update the URL via History API rather than a Vike navigation (see
 * contexts/SearchContext.tsx's trackSrpResultChange), so +title.ts never re-runs for them and
 * document.title goes stale. This recomputes just the title (not the full SEO meta, which needs
 * the full search-results payload for its description) from state already available client-side:
 * the current URL, the active filters, and the live result count.
 */
export function computeSearchPageTitleFromLiveState(
  url: string,
  locale: SupportedLocale,
  filters: unknown,
  jobCount: number | null,
): string {
  const safeLocale: SupportedLocale = SUPPORTED_LOCALES.includes(locale) ? locale : 'en';
  let pathnameForHub = '/';
  let searchParams = new URLSearchParams();
  try {
    const u = url.startsWith('http') ? new URL(url) : new URL(url, BASE_URL);
    pathnameForHub = u.pathname;
    searchParams = u.searchParams;
  } catch {
    // keep defaults
  }

  const staticOverride = getStaticUrlSeoOverride(pathnameForHub, searchParams);
  if (staticOverride?.title) return staticOverride.title;

  const activeFilters = buildActiveFiltersFromInitialFilters(filters, safeLocale);
  const pageNum = extractPageNumber(url);

  let title = truncateSeoTitle(generateTitle(activeFilters, jobCount, safeLocale));
  if (pageNum !== null && pageNum > 1) {
    title = truncateSeoTitle(title + getSearchPageTitleSuffix(pageNum, safeLocale));
  }
  return title;
}

/**
 * Computes SEO metadata for the search results page
 */
export function computeSearchPageMeta(pageContext: any, locale: SupportedLocale): SearchPageMeta {
  const baseUrl = BASE_URL;
  const safeLocale: SupportedLocale = SUPPORTED_LOCALES.includes(locale) ? locale : 'en';
  const urlOriginal = pageContext?.urlOriginal || '/';
  const url = typeof urlOriginal === 'string' ? urlOriginal : String(urlOriginal);
  let pathnameForHub: string;
  let searchParams: URLSearchParams;
  try {
    const u = url.startsWith('http') ? new URL(url) : new URL(url, baseUrl);
    pathnameForHub = u.pathname;
    searchParams = u.searchParams;
  } catch {
    pathnameForHub = '/';
    searchParams = new URLSearchParams();
  }

  const pathnameOnly = pathnameForHub === '/' ? '/' : pathnameForHub.replace(/\/$/, '');

  let canonicalUrl = (() => {
    try {
      const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;
      return generateSRPCanonicalUrl(fullUrl, baseUrl);
    } catch {
      return safeLocale === 'en' ? `${baseUrl}/` : `${baseUrl}/${safeLocale}`;
    }
  })();

  const pageNum = extractPageNumber(url);
  const srpHub = getSRPHub(pathnameOnly);
  if (srpHub) {
    let hubCanonical = `${baseUrl}${encodeSrpPathForUrl(srpHub.localeUrls[safeLocale])}`;
    if (pageNum !== null && pageNum > 1) {
      const withPage = new URL(hubCanonical);
      withPage.searchParams.set('page', pageParamForPageNumber(pageNum));
      hubCanonical = withPage.toString();
    }
    canonicalUrl = hubCanonical;
  }

  const srpViewModelNbHits = (pageContext as any)?.data?.srpViewModel?.nbHits;
  const initialResultsNbHits = (pageContext as any)?.data?.initialResults?.nbHits;
  const rawJobCount = typeof srpViewModelNbHits === 'number'
    ? srpViewModelNbHits
    : (typeof initialResultsNbHits === 'number' ? initialResultsNbHits : null);
  const jobCount = rawJobCount != null && rawJobCount > 0 ? rawJobCount : null;

  // Build all active filters from the server-normalised initialFilters (all types included)
  const activeFilters = buildActiveFiltersFromInitialFilters((pageContext as any)?.data?.initialFilters, safeLocale);

  // Override canonical URL for location-filtered pages to use the pretty path-based format.
  // Only for locations we can actually vouch for (curated city hub, a recognised remote scope,
  // or a real Finnish town per the GeoNames gazetteer) — the same gate the robots layer uses
  // (isVouchedLocationRouteSlug in +data.ts). Anything else (a corrupted/garbage location value
  // such as "Oulu, SuomiYritysOulun kaupunki" from a bad facet value) must NOT get a pretty
  // self-referencing canonical; those pages are noindexed, and the canonical should fall back to
  // the query form rather than reinforce a junk URL Google already found.
  const locationFilter = activeFilters.find(f => f.type === 'location');
  if (!srpHub && locationFilter) {
    const locationSlug = toLocationSlug(locationFilter.value);
    const isVouchedLocation =
      isKnownFinlandLocationSlug(locationSlug) ||
      isRemoteRegionalScopeForJobCard(locationFilter.value) ||
      isGazetteerFinnishTownSlug(locationSlug);
    const encodedSlug = buildEncodedLocationSrpPathSegment(locationFilter.value, safeLocale);
    if (encodedSlug && isVouchedLocation) {
        const jobsSegment = JOBS_SEGMENT_BY_LOCALE[safeLocale];
        const locationSegment = LOCATION_SEGMENT_BY_LOCALE[safeLocale];
        let locationCanonical = `${baseUrl}/${safeLocale}/${jobsSegment}/${locationSegment}/${encodedSlug}`;

        if (pageNum !== null && pageNum > 1) {
          const locationUrl = new URL(locationCanonical);
          locationUrl.searchParams.set('page', pageParamForPageNumber(pageNum));
          locationCanonical = locationUrl.toString();
        }

        canonicalUrl = locationCanonical;
    }
  }

  // Override canonical URL for category ("field")-filtered pages to use the pretty
  // path-based format (title/description already work — generate-srp-seo-meta.ts already
  // has full field grammar — this only fixes the canonical URL).
  const fieldFilter = activeFilters.find((f) => f.type === 'field');
  if (!srpHub && !locationFilter && fieldFilter) {
    const categorySlug = getCategorySlugByFieldValue(fieldFilter.value);
    if (categorySlug) {
      const jobsSegment = JOBS_SEGMENT_BY_LOCALE[safeLocale];
      const categorySegment = CATEGORY_SEGMENT_BY_LOCALE[safeLocale];
      let categoryCanonical = `${baseUrl}/${safeLocale}/${jobsSegment}/${categorySegment}/${categorySlug}`;

      if (pageNum !== null && pageNum > 1) {
        const categoryUrl = new URL(categoryCanonical);
        categoryUrl.searchParams.set('page', pageParamForPageNumber(pageNum));
        categoryCanonical = categoryUrl.toString();
      }

      canonicalUrl = categoryCanonical;
    }
  }

  const initialResults = (pageContext as any)?.data?.initialResults;
  const initialFiltersRaw = (pageContext as any)?.data?.initialFilters;
  const filtersForFacets: Record<string, string[]> =
    initialFiltersRaw &&
    typeof initialFiltersRaw === 'object' &&
    !Array.isArray(initialFiltersRaw)
      ? (initialFiltersRaw as Record<string, string[]>)
      : {};

  const aggregatedJobData = buildAggregatedJobDataFromSearchResults(initialResults, filtersForFacets, safeLocale as 'en' | 'fi' | 'sv');
  const jobData: AggregatedJobData = {
    count: jobCount,
    ...aggregatedJobData,
  };
  // Location pages: salary from the nightly SalaryStats aggregate (see applyLocationSalaryAggregate).
  const salaryAggregate = (pageContext as any)?.data?.locationTrendsData?.salaryStats?.aggregate ?? null;
  const seoMeta = generateSEOMeta(activeFilters, applyLocationSalaryAggregate(jobData, activeFilters, salaryAggregate), safeLocale, canonicalUrl);
  const staticOverride = getStaticUrlSeoOverride(pathnameForHub, searchParams);

  return {
    canonicalUrl,
    title: staticOverride?.title ?? seoMeta.title,
    description: staticOverride?.description ?? seoMeta.description,
  };
}
